import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import styled from '@mui/material/styles/styled';
import { useTranslation } from 'react-i18next';
import { WalkIcon, CaloryIcon, TimeIcon } from '../../../../assets/images/activityIcons';
import StatusIndicator from '../../../../components/statusIndicator';
import ExerciseDataItem from '../../../../pages/reports/components/exerciseDataItem';
import DonutChart from '../../../../components/charts/donutChart';
import { EXERCISE_TARGETS } from '../../../../constants';
import { calculatePercentage, formatDateTime, formatDateTimeDisplay } from '../../../../utils/base';

// Styled components
const PaperCard = styled(Box)(({ theme, background }) => ({
    borderRadius: '20px',
    padding: '20px',
    boxShadow: theme.palette.shadows?.custom?.card || '0px 2px 8px rgba(0, 0, 0, 0.1)',
    border: 'none',
    position: 'relative',
    background: background || theme.palette.figma.block,
}));

const WalkingDataCard = ({
    physicalRecord = {},
    onClick = () => { },
    sx = {},
    background = null
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [connectionStatus, setConnectionStatus] = useState(false);
    const meInfo = JSON.parse(localStorage.getItem('meInfo'));

    // 오늘 날짜 확인 (UTC+9 기준)
    const isToday = () => {
        if (!physicalRecord?.date) return false;

        const now = formatDateTime(new Date(), '', '', 0, 8);
        const recordDate = formatDateTime(physicalRecord.date, '', '', 0, 8);

        return now === recordDate;
    };

    // 앱으로 로그 전송 함수
    const sendLogToApp = (logMessage, ...args) => {
        if (window.ReactNativeWebView) {
            const logData = args.length > 0 ? [logMessage, ...args] : [logMessage];
            // window.ReactNativeWebView.postMessage(JSON.stringify({
            //     type: 'console',
            //     data: logData
            // }));
        }
        // 웹 콘솔에도 출력
        // console.log(logMessage, ...args);
    };

    // 앱에서 연결 상태 데이터 가져오기
    useEffect(() => {
        const requestConnectionStatus = () => {
            if (window.ReactNativeWebView) {
                sendLogToApp('[WalkingDataCard] 연결 상태 요청 전송');
                // 앱에 연결 상태 요청 메시지 전송
                window.ReactNativeWebView.postMessage(JSON.stringify({
                    type: 'requestConnectionStatus',
                    data: {}
                }));
            } else {
                sendLogToApp('[WalkingDataCard] ReactNativeWebView가 없습니다');
            }
        };

        // 메시지 리스너 설정
        const handleMessage = (event) => {
            try {
                sendLogToApp('[WalkingDataCard] 메시지 수신:', event.data);

                // webpack hot reload 메시지나 다른 시스템 메시지 필터링
                if (typeof event.data === 'string') {
                    // JSON 형태가 아닌 메시지들 필터링
                    if (event.data.startsWith('webpackHot') ||
                        event.data.startsWith('webpack') ||
                        event.data.startsWith('{') === false) {
                        sendLogToApp('[WalkingDataCard] 시스템 메시지 필터링:', event.data);
                        return;
                    }

                    const message = JSON.parse(event.data);
                    sendLogToApp('[WalkingDataCard] 파싱된 메시지:', message);

                    if (message?.type === 'connectionStatus') {
                        const status = message?.data === 'true' ? true : false;
                        sendLogToApp('[WalkingDataCard] 연결 상태 업데이트:', status);
                        setConnectionStatus(status);
                    }
                }
            } catch (error) {
                // JSON 파싱 실패는 무시 (시스템 메시지일 가능성)
                sendLogToApp('[WalkingDataCard] 메시지 파싱 실패:', error.message, event.data);
            }
        };

        // 앱에서 연결 상태 요청
        sendLogToApp('[WalkingDataCard] useEffect 실행 - 연결 상태 요청 시작');
        requestConnectionStatus();

        // 메시지 리스너 등록
        sendLogToApp('[WalkingDataCard] 메시지 리스너 등록');
        window.addEventListener('message', handleMessage);

        // 컴포넌트 언마운트 시 리스너 제거
        return () => {
            sendLogToApp('[WalkingDataCard] 컴포넌트 언마운트 - 리스너 제거');
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    return (
        <PaperCard sx={sx} background={background} onClick={onClick}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    alignItems: 'center',
                    width: '100%',
                }}
            >
                {/* Connection Status */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                        }}
                    >
                        {meInfo?.grade == 1 &&
                            <StatusIndicator
                                status={
                                    connectionStatus
                                        ? 'connect'
                                        : 'disconnect'
                                }

                            />
                        }
                        <Typography
                            sx={{
                                fontSize: '12px',
                                color: theme.palette.figma.white[60],
                                fontWeight: 400,
                                letterSpacing: '-0.36px',
                            }}
                        >
                            {physicalRecord?.lastDate && (() => {
                                if (isToday()) {
                                    return `${t('last_sync')}: ${formatDateTimeDisplay(physicalRecord.lastDate, 'time')}`;
                                }
                                return `${t('last_sync')}: ${formatDateTimeDisplay(physicalRecord.lastDate, 'datetime')}`;
                            })()}
                        </Typography>
                    </Box>
                </Box>

                {/* Data Section - 항상 표시 */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '5px',
                        alignItems: 'flex-start',
                        width: '100%',
                        borderRadius: '10px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                                minWidth: '150px', // 5자리 숫자(99,999디딤)까지 표시 가능하도록 최소 너비 증가
                                flex: 1, // 남은 공간 활용
                                maxWidth: '180px', // 최대 너비 제한
                            }}
                        >
                            {/* Steps */}
                            <ExerciseDataItem
                                icon={WalkIcon}
                                value={isToday() ? (physicalRecord?.totalSteps || 0) : 0}
                                unit={t('didim')}
                            />

                            {/* Calories */}
                            <ExerciseDataItem
                                icon={CaloryIcon}
                                value={isToday() ? (physicalRecord?.calories || 0) : 0}
                                unit="kcal"
                            />

                            {/* Time */}
                            <ExerciseDataItem
                                icon={TimeIcon}
                                value={isToday() ? (physicalRecord?.totalTime || 0) : 0}
                                unit={t('min')}
                            />
                        </Box>

                        {/* Overlapping Donut Charts */}
                        <DonutChart
                            data={[
                                {
                                    label: t('didim'),
                                    value: calculatePercentage(
                                        isToday() ? (physicalRecord?.totalSteps || 0) : 0,
                                        EXERCISE_TARGETS.STEPS
                                    ),
                                    color: theme.palette.figma.steps,
                                    radius: 45,
                                    strokeWidth: 10,
                                },
                                {
                                    label: t('calories'),
                                    value: calculatePercentage(
                                        isToday() ? (physicalRecord?.calories || 0) : 0,
                                        EXERCISE_TARGETS.CALORIES
                                    ),
                                    color: theme.palette.figma.calorieRed,
                                    radius: 35,
                                    strokeWidth: 10,
                                },
                                {
                                    label: t('time'),
                                    value: calculatePercentage(
                                        isToday() ? (physicalRecord?.totalTime || 0) : 0,
                                        EXERCISE_TARGETS.ACTIVITY_TIME
                                    ),
                                    color: theme.palette.figma.time,
                                    radius: 25,
                                    strokeWidth: 10,
                                },
                            ]}
                            size={100}
                            centerRadius={60}
                        />
                    </Box>
                </Box>
            </Box>
        </PaperCard>
    );
};

export default WalkingDataCard;
